import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const linkClass = scrolled || !isHome
    ? "text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
    : "text-sm font-medium text-background-50/90 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer";

  const brandClass = scrolled || !isHome
    ? "font-heading text-lg md:text-xl font-semibold text-foreground-900 whitespace-nowrap"
    : "font-heading text-lg md:text-xl font-semibold text-background-50 whitespace-nowrap";

  const brandIconBg = scrolled || !isHome
    ? "bg-primary-500"
    : "bg-background-50/25 backdrop-blur-sm";

  const brandIconText = scrolled || !isHome
    ? "text-background-50"
    : "text-background-50";

  const ctaBtnClass = scrolled || !isHome
    ? "hidden md:inline-flex items-center px-4 py-2 text-sm font-medium bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
    : "hidden md:inline-flex items-center px-4 py-2 text-sm font-medium bg-background-50 text-foreground-900 rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer";

  const mobileToggleColor = scrolled || !isHome
    ? "text-foreground-700"
    : "text-background-50";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between h-16 md:h-20 px-4 md:px-10">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <div className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center ${brandIconBg} rounded-lg`}>
            <i className={`ri-database-2-line ${brandIconText} text-lg`}></i>
          </div>
          <span className={brandClass}>
            OOPBuy Spreadsheet
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-7">
          <Link to="/" className={linkClass}>
            Home
          </Link>
          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-1 ${linkClass}`}
            >
              {t("nav.categories")}
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
            </button>
            {dropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-background-50 border border-background-200/80 rounded-xl shadow-sm py-2 z-50 max-h-[420px] overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                    {cat.isSearch && <span className="ml-1 text-xs text-foreground-400">(search)</span>}
                  </a>
                ))}
              </div>
            )}
          </div>
          <Link to="/shopping-guide" className={linkClass}>
            Shopping Guide
          </Link>
          <a href="#faq" className={linkClass}>
            {t("nav.faq")}
          </a>
          <Link to="/oopbuy-spreadsheet-complete-guide" className={linkClass}>
            Guide
          </Link>
        </div>

        <div className="flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={ctaBtnClass}
          >
            Buy It Now
          </a>
          <button
            className={`md:hidden w-9 h-9 flex items-center justify-center ${mobileToggleColor} cursor-pointer`}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70 px-5 pb-5 pt-2">
          <div className="flex flex-col gap-0.5">
            <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer">
              <i className="ri-home-4-line text-base w-5 h-5 flex items-center justify-center"></i>
              Home
            </Link>
            <Link to="/shopping-guide" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer">
              <i className="ri-shopping-bag-3-line text-base w-5 h-5 flex items-center justify-center"></i>
              Shopping Guide
            </Link>
            <p className="pt-3 pb-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Categories</p>
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="py-2.5 pl-4 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer border-l-2 border-transparent hover:border-primary-300"
              >
                {cat.name}
                {cat.isSearch && <span className="ml-1.5 text-xs text-foreground-400">(search)</span>}
              </a>
            ))}
            <div className="border-t border-background-200/50 my-2"></div>
            <a href="#faq" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer">
              <i className="ri-question-line text-base w-5 h-5 flex items-center justify-center"></i>
              {t("nav.faq")}
            </a>
            <Link to="/oopbuy-spreadsheet-complete-guide" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer">
              <i className="ri-book-open-line text-base w-5 h-5 flex items-center justify-center"></i>
              Guide
            </Link>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="mt-2 flex items-center justify-center gap-2 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-shopping-cart-line"></i>
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}